import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  WhiteLabelConfig,
  WhiteLabelThemeConfig,
} from '../models/whitelabel.model';
import { WHITELABEL_CONFIG } from '../config/whitelabel.config';

@Injectable({ providedIn: 'root' })
export class WhiteLabelService {
  private _config: WhiteLabelConfig = this.deepClone(WHITELABEL_CONFIG);
  private _effectiveTheme: 'light' | 'dark' = 'light';

  private readonly configSubject = new BehaviorSubject<WhiteLabelConfig>(this._config);
  readonly config$: Observable<WhiteLabelConfig> = this.configSubject.asObservable();

  get config(): WhiteLabelConfig {
    return this._config;
  }

  get activeThemeConfig(): WhiteLabelThemeConfig {
    return this._effectiveTheme === 'dark'
      ? this._config.darkTheme
      : this._config.lightTheme;
  }

  get logoUrl(): string {
    return this.activeThemeConfig.logos.logo;
  }

  get logoSmallUrl(): string {
    return this.activeThemeConfig.logos.logoSmall;
  }

  get logoLoginUrl(): string {
    return this.activeThemeConfig.logos.logoLogin;
  }

  get appName(): string {
    return this._config.appName;
  }

  get footerText(): string {
    return this._config.footerText;
  }

  init(): void {
    this.applyConfig();
  }

  applyUserOverrides(overrides: Partial<WhiteLabelConfig>): void {
    this._config = this.mergeDeep(this.deepClone(WHITELABEL_CONFIG), overrides);
    this.configSubject.next(this._config);
    this.applyConfig();
  }

  onThemeChange(theme: 'light' | 'dark'): void {
    this._effectiveTheme = theme;
    this.applyThemeSpecificStyles();
  }

  private applyConfig(): void {
    this.applyCssVariables();
    this.applyThemeSpecificStyles();
    this.applyFavicon();
    this.applyTitle();
  }

  private applyCssVariables(): void {
    const root = document.documentElement;
    root.style.setProperty('--wl-primary', this._config.primaryColor);
    root.style.setProperty('--wl-secondary', this._config.secondaryColor);
    root.style.setProperty('--bs-primary', this._config.primaryColor);
    root.style.setProperty('--bs-primary-rgb', this.hexToRgb(this._config.primaryColor));
  }

  private applyThemeSpecificStyles(): void {
    const root = document.documentElement;
    const themeConfig = this.activeThemeConfig;

    root.style.setProperty('--wl-header-bg', themeConfig.header.backgroundColor);
    root.style.setProperty('--wl-header-text', themeConfig.header.textColor);
    root.style.setProperty('--wl-sidebar-bg', themeConfig.sidebar.backgroundColor);
    root.style.setProperty('--wl-sidebar-item-color', themeConfig.sidebar.menuItemColor);
    root.style.setProperty('--wl-sidebar-item-hover', themeConfig.sidebar.menuItemHoverColor);
    root.style.setProperty('--wl-sidebar-item-active', themeConfig.sidebar.menuItemActiveColor);
    root.style.setProperty('--wl-sidebar-icon-color', themeConfig.sidebar.iconColor);
  }

  private applyFavicon(): void {
    const themeLogos = this.activeThemeConfig.logos;
    const link: HTMLLinkElement =
      document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = themeLogos.favicon;
    document.head.appendChild(link);
  }

  private applyTitle(): void {
    document.title = this._config.appName;
  }

  private hexToRgb(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return '85, 110, 230';
    return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
  }

  private deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  private mergeDeep(target: any, source: any): any {
    if (!source) return target;
    const output = { ...target };
    for (const key of Object.keys(source)) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        output[key] = this.mergeDeep(target[key] || {}, source[key]);
      } else if (source[key] !== undefined) {
        output[key] = source[key];
      }
    }
    return output;
  }
}
